import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { trackHeroAdded } from '../../lib/analytics';
import {
  type Characteristics,
  createHero,
  type Hero,
  updateCharacteristics,
} from '../../lib/heroes';
import { parseHeroAgeYears } from '../../lib/heroAge';
import {
  inferPersonType,
  isPersonHero,
  PERSON_TYPE_OPTIONS,
} from '../../lib/heroKind';
import { RadioGroup } from './RadioGroup';

type Mode =
  | { kind: 'upload' }
  | { kind: 'analyzing' }
  | { kind: 'confirm'; hero: Hero };

type AddHeroStepProps = {
  onSaved: (hero: Hero) => void;
  onCancel: () => void;
};

export function AddHeroStep({ onSaved, onCancel }: AddHeroStepProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>({ kind: 'upload' });
  const [error, setError] = useState<string | null>(null);

  async function handleAnalyze(name: string, photo: File) {
    setError(null);
    setMode({ kind: 'analyzing' });
    try {
      const hero = await createHero(name, photo);
      trackHeroAdded(hero.id);
      setMode({ kind: 'confirm', hero });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('wizard.uploadFailed'));
      setMode({ kind: 'upload' });
    }
  }

  return (
    <div className="wizard-step" data-step="add-hero">
      <h2 className="wizard-step__title">
        {mode.kind === 'confirm' ? t('wizard.confirmTitle') : t('wizard.addHeroTitle')}
      </h2>
      <p className="wizard-step__hint">
        {mode.kind === 'confirm' ? t('wizard.confirmHint') : t('wizard.addHeroHint')}
      </p>

      {mode.kind === 'upload' && (
        <UploadPanel onAnalyze={handleAnalyze} onCancel={onCancel} error={error} />
      )}
      {mode.kind === 'analyzing' && (
        <div className="hero-analyze">
          <div className="hero-analyze__spinner" aria-hidden="true" />
          <p>{t('wizard.analyzing')}</p>
        </div>
      )}
      {mode.kind === 'confirm' && (
        <ConfirmPanel
          hero={mode.hero}
          onSaved={onSaved}
          onBack={() => setMode({ kind: 'upload' })}
        />
      )}
    </div>
  );
}

function UploadPanel({
  onAnalyze,
  onCancel,
  error,
}: {
  onAnalyze: (name: string, photo: File) => void;
  onCancel: () => void;
  error: string | null;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);

  const previewUrl = useMemo(
    () => (photo ? URL.createObjectURL(photo) : null),
    [photo],
  );
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!name.trim() || !photo) return;
    onAnalyze(name.trim(), photo);
  }

  return (
    <form className="hero-upload" onSubmit={handleSubmit}>
      <label className={`hero-upload__photo${previewUrl ? ' hero-upload__photo--filled' : ''}`}>
        {previewUrl ? (
          <img src={previewUrl} alt={t('wizard.previewAlt')} />
        ) : (
          <span className="hero-upload__photo-hint">
            <span className="hero-upload__photo-plus">+</span>
            <span>{t('wizard.choosePhoto')}</span>
          </span>
        )}
        <input
          type="file"
          accept="image/*"
          required
          onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
        />
      </label>

      <label className="field">
        <span className="field__label">{t('common.name')}</span>
        <input
          type="text"
          className="field__input"
          placeholder={t('wizard.namePlaceholder')}
          required
          maxLength={150}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>

      {error && <p className="form-error">{error}</p>}

      <div className="wizard-footer">
        <button type="button" className="btn btn--ghost" onClick={onCancel}>
          {t('common.back')}
        </button>
        <button
          type="submit"
          className="btn btn--primary"
          disabled={!name.trim() || !photo}
        >
          {t('wizard.analyzePhoto')}
        </button>
      </div>
    </form>
  );
}

function ConfirmPanel({
  hero,
  onSaved,
  onBack,
}: {
  hero: Hero;
  onSaved: (h: Hero) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const isPerson = isPersonHero(hero.characteristics);
  const initialAge = parseHeroAgeYears(hero.characteristics);

  const [name, setName] = useState(hero.name);
  const [personType, setPersonType] = useState<string>(() =>
    inferPersonType(hero.characteristics),
  );
  const [ageNum, setAgeNum] = useState<string>(initialAge !== null ? String(initialAge) : '');
  const [typeText, setTypeText] = useState<string>(hero.characteristics?.type ?? '');
  const [ageText, setAgeText] = useState<string>(hero.characteristics?.age ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const next: Characteristics = { ...(hero.characteristics ?? {}) };
      if (isPerson) {
        next.type = personType;
        next.age = ageNum ? `${ageNum} years old` : '';
      } else {
        next.type = typeText.trim();
        next.age = ageText.trim();
      }
      const updated = await updateCharacteristics(hero.id, next);
      const final = name.trim() && name.trim() !== updated.name ? { ...updated, name: name.trim() } : updated;
      onSaved(final);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('wizard.saveFailed'));
      setSaving(false);
    }
  }

  return (
    <div className="hero-confirm">
      <div className="hero-confirm__photo">
        <img src={hero.photo} alt={hero.name} />
      </div>

      <label className="field">
        <span className="field__label">{t('common.name')}</span>
        <input
          type="text"
          className="field__input"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>

      {isPerson ? (
        <>
          <RadioGroup
            label={t('wizard.whoIsIt')}
            name="person-type"
            value={personType}
            options={PERSON_TYPE_OPTIONS}
            onChange={setPersonType}
          />
          <label className="field field--inline">
            <span className="field__label">{t('wizard.age')}</span>
            <span className="field__input-with-suffix">
              <input
                type="number"
                min={0}
                max={120}
                className="field__input"
                value={ageNum}
                onChange={(e) => setAgeNum(e.target.value)}
              />
              <span className="field__suffix">{t('wizard.ageSuffix')}</span>
            </span>
          </label>
        </>
      ) : (
        <>
          <label className="field">
            <span className="field__label">{t('wizard.whatIsIt')}</span>
            <input
              type="text"
              className="field__input"
              placeholder={t('wizard.typePlaceholder')}
              value={typeText}
              onChange={(e) => setTypeText(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field__label">{t('wizard.ageOptional')}</span>
            <input
              type="text"
              className="field__input"
              placeholder={t('wizard.agePlaceholder')}
              value={ageText}
              onChange={(e) => setAgeText(e.target.value)}
            />
          </label>
        </>
      )}

      {hero.characteristics?.appearance && (
        <p className="hero-confirm__appearance">{hero.characteristics.appearance}</p>
      )}

      {error && <p className="form-error">{error}</p>}

      <div className="wizard-footer">
        <button type="button" className="btn btn--ghost" onClick={onBack} disabled={saving}>
          {t('common.retake')}
        </button>
        <button
          type="button"
          className="btn btn--primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? t('common.saving') : t('wizard.saveHero')}
        </button>
      </div>
    </div>
  );
}
